import { useState } from 'react'
import Icon from '../Icon'
import { api, go, useContent, type BadgeView, type Me } from '../../lib/classroom'
import { L, w } from '../../lib/words'
import { celebrate, sound } from '../../lib/fun'
import { MILESTONE_XP, TIER_XP } from '../../../shared/classroom'
import Pichi, { stageFor } from './Pichi'
import { CodeInput, LevelSheet, Sheet } from './Sheets'

/**
 * The student's home. Everything is tappable: the level card opens the
 * ladder, the XP pill opens the shop, the badges pill jumps to the
 * badges. Joining a class lives on the bottom bar (App), or here in big
 * letters while the student has no class yet.
 */
export default function StudentHome({ me, name, refresh }: { me: Me; name: string; refresh: () => Promise<void> }) {
  const modules = useContent()
  const [code, setCode] = useState('')
  const [joinError, setJoinError] = useState<string | null>(null)
  const [ladder, setLadder] = useState(false)
  const [badge, setBadge] = useState<BadgeView | null>(null)

  const join = async () => {
    setJoinError(null)
    try {
      await api.join(code)
      setCode('')
      celebrate()
      sound('level')
      await refresh()
    } catch (e) {
      setJoinError((e as Error).message)
    }
  }

  const tiers = me.badges.reduce((n, b) => n + b.tier, 0)
  const maxTiers = me.badges.length * 3
  const lvl = me.level
  const first = name.split(' ')[0]

  return (
    <div className="student-home">
      {me.live.map((r) => (
        <button key={r.id} className="live-banner" onClick={() => go(`/live/${r.id}`)}>
          <span className="pulse" />
          <span><strong>{w('liveNow')}</strong><br /><span className="small">{L(lessonTitle(modules, r.lesson_id))}</span></span>
          <span className="live-go">{w('enter')} <Icon name="chevron" /></span>
        </button>
      ))}

      <button className={`card me-card bg-${me.look.bg ?? 'mar'}`} onClick={() => { sound('tap'); setLadder(true) }}>
        <Pichi size={116} stage={stageFor(lvl.index)} mood={me.xp === 0 ? 'wave' : 'happy'} look={me.look} />
        <span className="me-text">
          <span className="muted small">{w('hi')}, {first}!</span>
          <span className="level-chip">{w('level')} {lvl.index + 1}</span>
          <span className="level-title">{L(lvl.title)}</span>
          <span className="xp-bar"><span style={{ width: `${Math.max(4, Math.round(lvl.progress * 100))}%` }} /></span>
          <span className="small"><b>{me.xp.toLocaleString()} {w('xpTotal')}</b> · {lvl.next ? `${(lvl.next - me.xp).toLocaleString()} ${w('toNext')} ${w('level').toLowerCase()} ${lvl.index + 2}` : w('maxLevel')}</span>
        </span>
        <Icon name="chevron" className="me-go" />
      </button>

      <div className="pills">
        <button className="pill pill-oro" onClick={() => go('/tienda')}>
          <Icon name="coin" /><b>{me.wallet.toLocaleString()}</b><span>{w('xpSpend')}</span>
        </button>
        <button className="pill" onClick={() => setLadder(true)}>
          <Icon name="flame" /><b>{me.stats.bestStreak}</b><span>{w('streak')}</span>
        </button>
        <button className="pill" onClick={() => document.getElementById('badges')?.scrollIntoView({ behavior: 'smooth' })}>
          <Icon name="trophy" /><b>{tiers}/{maxTiers}</b><span>{w('badges')}</span>
        </button>
      </div>

      {me.classes.length === 0 && (
        <section className="card join-card">
          <h2><Icon name="people" /> {w('joinAClass')}</h2>
          <CodeInput value={code} onChange={setCode} onEnter={join} />
          <p className="muted small center">{w('codeHint')}</p>
          <button className="primary big" disabled={code.length < 3} onClick={join}>{w('join')} <Icon name="chevron" /></button>
          {joinError && <p className="error">{joinError}</p>}
        </section>
      )}

      <button className="card shop-promo" onClick={() => go('/tienda')}>
        <Pichi size={72} stage={Math.max(1, stageFor(lvl.index))} look={{ ...me.look, hat: me.look.hat ?? 'pava', glasses: me.look.glasses ?? 'gafas' }} mood="wave" />
        <span><strong>{w('shop')}</strong><span className="small">{w('shopSub')}</span></span>
        <Icon name="chevron" />
      </button>

      <section className="card">
        <h2><Icon name="book" /> {w('study')}</h2>
        <p className="muted small">{w('studyNote')}</p>
        {modules?.map((m) => (
          <div key={m.module.id}>
            <h3 className="module-title">{L(m.module.title)}</h3>
            <ul className="lesson-grid">
              {m.lessons.map((l, i) => {
                const qs = l.steps.filter((s) => s.type === 'question').length
                const done = me.practiced[l.id] ?? 0
                const taught = me.taught.includes(l.id)
                return (
                  <li key={l.id}>
                    <button className={`lesson-card t${i % 8}${taught ? ' taught' : ''}`} onClick={() => go(`/study/${l.id}`)}>
                      {taught && (
                        <span className="stamp" aria-label={w('doneInClass')}>
                          <span>{w('doneInClass')}</span>
                          <Icon name="check" size={34} />
                        </span>
                      )}
                      <span className="lesson-num">{l.id}</span>
                      <span className="lesson-name">{L(l.title)}</span>
                      <span className="meta">{done >= qs && qs > 0 ? <><Icon name="star" size={14} /> {w('practiceDoneTag')}</> : `${w('practiceProgress')}: ${done}/${qs}`}</span>
                      <span className="mini-bar"><span style={{ width: `${qs ? Math.round((done / qs) * 100) : 0}%` }} /></span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </section>

      <section className="card" id="badges">
        <h2><Icon name="trophy" /> {w('badges')} <span className="muted small">{tiers}/{maxTiers}</span></h2>
        <p className="muted small">{w('badgesSub')}</p>
        <div className="badges">
          {me.badges.map((b) => (
            <button key={b.key} className={`badge tier-${b.tier}`} onClick={() => { sound('tap'); setBadge(b) }}>
              <span className="medal"><Icon name={b.tier ? 'star' : 'lock'} size={24} /></span>
              <span>{L(b.title)}</span>
              <span className="pips">{[1, 2, 3].map((t) => <i key={t} className={t <= b.tier ? `on t${t}` : ''} />)}</span>
            </button>
          ))}
        </div>
        {me.champion && <p className="champion"><Icon name="trophy" /> {w('championGot')}</p>}
      </section>

      <section className="card">
        <h2><Icon name="wallet" /> {w('milestones')}</h2>
        <p className="muted small">{w('milestonesNote')}</p>
        <ul className="milestones">
          {me.milestones.map((m) => (
            <li key={m.key} className={m.doneAt ? 'done' : ''}>
              <span className="ms-mark">{m.doneAt ? <Icon name="check" size={20} /> : <Icon name="lock" size={18} />}</span>
              <span className="ms-name">
                {L(m.title)}
                <span className="small muted">{m.doneAt ? w('verified') : `${w('showTeacher')} ${MILESTONE_XP} ${w('oro')}`}</span>
              </span>
              <span className="ms-oro"><Icon name="coin" size={16} /> {MILESTONE_XP}</span>
            </li>
          ))}
        </ul>
      </section>

      <LevelSheet me={me} open={ladder} onClose={() => setLadder(false)} />
      <Sheet open={!!badge} onClose={() => setBadge(null)} title={badge ? L(badge.title) : ''}>
        {badge && (
          <div className="center badge-detail">
            <span className={`medal big tier-${badge.tier}`}><Icon name={badge.tier ? 'star' : 'lock'} size={48} /></span>
            <h2>{L(badge.title)}{badge.tier ? ` · ${L(badge.tierName)}` : ''}</h2>
            <p>{badge.next != null ? L(badge.desc).replace('{n}', String(badge.next)) : w('badgeMaxed')}</p>
            {badge.next != null && (
              <div className="badge-progress">
                <div className="xp-bar"><span style={{ width: `${Math.min(100, Math.round((badge.value / badge.next) * 100))}%` }} /></div>
                <span className="small">{badge.value} / {badge.next}</span>
              </div>
            )}
            <ol className="tier-list">
              {badge.tiers.map((n, k) => (
                <li key={k} className={k < badge.tier ? 'got' : ''}>
                  <span className={`dot t${k + 1}`} />
                  <span>{[w('bronze'), w('silver'), w('gold')][k]} · {L(badge.desc).replace('{n}', String(n))}</span>
                  <b>+{TIER_XP[k + 1]} XP</b>
                </li>
              ))}
            </ol>
            <p className="muted small">{w('championHint')}</p>
          </div>
        )}
      </Sheet>
    </div>
  )
}

export function lessonTitle(modules: ReturnType<typeof useContent>, id: string) {
  for (const m of modules ?? []) for (const l of m.lessons) if (l.id === id) return l.title
  return { es: id, en: id }
}
